import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Heart, User, Search, Menu, X, Bell, LogOut, ChevronDown, Store } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { categoryAPI } from '../../api';

const Navbar = () => {
  const { user, isLoggedIn, isAdmin, isSeller, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setMenuOpen(false);
    }
  };

  const { data: categoryData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryAPI.getAll().then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });
  const categories = categoryData?.categories || [];

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Top banner */}
      <div style={{ backgroundColor: '#f43397', color: 'white', textAlign: 'center', padding: '6px', fontSize: '13px', fontWeight: 500 }}>
        🎉 Free delivery on orders above ₹499 | Use code WELCOME50 for 50% off!
      </div>

      {/* Main navbar */}
      <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Logo */}
          <Link to="/" style={{ flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg, #f43397, #6c3fc5)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Store size={20} color="white" />
              </div>
              <span style={{ fontSize: 22, fontWeight: 800, background: 'linear-gradient(to right, #f43397, #6c3fc5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                ENew Aura Solutions
              </span>
            </div>
          </Link>

          {/* Search bar */}
          <form onSubmit={handleSearch} style={{ flex: 1, maxWidth: 500, position: 'relative' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search for products, brands and more"
              style={{
                width: '100%', padding: '10px 48px 10px 16px',
                border: '2px solid #f43397', borderRadius: 8,
                fontSize: 14, outline: 'none', backgroundColor: '#fff5fb'
              }}
            />
            <button type="submit" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#f43397', cursor: 'pointer' }}>
              <Search size={20} />
            </button>
          </form>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            {isLoggedIn ? (
              <>
                <Link to="/wishlist" style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6, color: '#374151', fontSize: 13, fontWeight: 500 }}>
                  <Heart size={20} />
                  <span className="hide-mobile">Wishlist</span>
                </Link>
                {/* User menu */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: userMenuOpen ? '#f9f0fb' : 'transparent', color: '#374151', fontSize: 13, fontWeight: 500 }}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#f43397', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                      {user?.name?.[0]?.toUpperCase()}
                    </div>
                    <span className="hide-mobile">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown size={16} />
                  </button>
                  {userMenuOpen && (
                    <div style={{ position: 'absolute', right: 0, top: '110%', background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 200 }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{user?.name}</p>
                        <p style={{ fontSize: 12, color: '#6b7280' }}>{user?.email}</p>
                      </div>
                      {[
                        { to: '/profile', icon: <User size={15} />, label: 'My Profile' },
                        { to: '/wishlist', icon: <Heart size={15} />, label: 'Wishlist' },
                        ...(isSeller ? [{ to: '/seller', icon: <Store size={15} />, label: 'Seller Dashboard' }] : []),
                        ...(isAdmin ? [{ to: '/admin', icon: <Bell size={15} />, label: 'Admin Panel' }] : []),
                      ].map((item) => (
                        <Link key={item.to} to={item.to} onClick={() => setUserMenuOpen(false)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: 14, color: '#374151', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f9f9f9'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          {item.icon} {item.label}
                        </Link>
                      ))}
                      <div style={{ borderTop: '1px solid #f0f0f0' }}>
                        <button onClick={() => { logout(); setUserMenuOpen(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', width: '100%', fontSize: 14, color: '#ef4444', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' }}
                        >
                          <LogOut size={15} /> Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Register</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Category bar */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #f0f0f0', overflowX: 'auto' }} className="hide-mobile">
        <div className="container">
          <div style={{ display: 'flex', gap: 0 }}>
            {categories.map(cat => (
              <Link
                key={cat.id}
                to={`/products?category=${cat.slug}`}
                style={{ padding: '10px 18px', fontSize: 13, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap', borderBottom: '2px solid transparent', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#f43397'; e.currentTarget.style.borderBottomColor = '#f43397'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderBottomColor = 'transparent'; }}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Click outside handler */}
      {userMenuOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 100 }} onClick={() => setUserMenuOpen(false)} />}
    </header>
  );
};

export default Navbar;
